import { SAFE_NAV_URL, type StorefrontInfo, type StorefrontNavItem } from '@erp/shared';
import { Check, Globe, Heart, Menu, Moon, Palette, ShoppingCart, Store, Sun, UserCircle2, X } from 'lucide-react';
import { createContext, useContext, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';

import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, Toaster } from '../components/shadcn/index.js';
import { RouteTransition } from '../components/ui/index.js';
import { StorefrontAiWidget } from '../features/storefront/StorefrontAiWidget.js';
import { StorefrontSearchModal } from '../features/storefront/StorefrontSearchModal.js';
import { setLocale, useDirection } from '../hooks/useDirection.js';
import { useTheme } from '../hooks/useTheme.js';
import { cartCount, useCartStore } from '../lib/cartStore.js';
import { useCustomerAuthStore } from '../lib/customerAuthStore.js';
import { setCurrencyDecimals } from '../lib/money.js';
import { assetUrl, publicApi } from '../lib/publicApi.js';
import { deriveStoreTheme, fontVar } from '../lib/storeTheme.js';
import { setStoreAccent, SF_ACCENTS, useStoreAccent } from '../lib/storeUserTheme.js';
import { useWishlistStore, wishlistCount } from '../lib/wishlistStore.js';

import '../styles/storefront.css';

interface StorefrontCtx {
  slug: string;
  site: StorefrontInfo;
}
const Ctx = createContext<StorefrontCtx | null>(null);

/** Access the resolved storefront (site info + slug) inside any public page. */
export function useStorefront(): StorefrontCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useStorefront must be used within StorefrontLayout');
  return c;
}

/** Render-time guard: never emit an href with an unsafe scheme (defense in depth). */
function safeHref(url: string): string {
  return SAFE_NAV_URL.test(url.trim()) ? url : '#';
}

/** WhatsApp link: accept a full URL as-is, else build a wa.me link from the digits. */
function waHref(v: string): string {
  const s = v.trim();
  if (/^https?:\/\//i.test(s)) return safeHref(s);
  const digits = s.replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : '#';
}

/** Brand glyphs lucide no longer ships (single-path, currentColor). */
function IgIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z" />
    </svg>
  );
}
function FbIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z" />
    </svg>
  );
}
function WaIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91C2.13 13.66 2.59 15.36 3.45 16.86L2.05 22L7.3 20.62C8.75 21.41 10.38 21.83 12.04 21.83C17.5 21.83 21.95 17.38 21.95 11.92C21.95 9.27 20.92 6.78 19.05 4.91C17.18 3.03 14.69 2 12.04 2M12.05 3.67C14.25 3.67 16.31 4.53 17.87 6.09C19.42 7.65 20.28 9.72 20.28 11.92C20.28 16.46 16.58 20.15 12.04 20.15C10.56 20.15 9.11 19.76 7.85 19L7.55 18.83L4.43 19.65L5.26 16.61L5.06 16.29C4.24 15 3.8 13.47 3.8 11.91C3.8 7.37 7.5 3.67 12.05 3.67M8.53 7.33C8.37 7.33 8.1 7.39 7.87 7.64C7.65 7.89 7 8.5 7 9.71C7 10.93 7.89 12.1 8.01 12.27C8.14 12.44 9.76 14.94 12.25 16C12.84 16.27 13.3 16.42 13.66 16.53C14.25 16.72 14.79 16.69 15.22 16.63C15.7 16.56 16.68 16.03 16.89 15.45C17.1 14.87 17.1 14.38 17.04 14.27C16.97 14.17 16.81 14.11 16.56 14C16.31 13.86 15.09 13.26 14.87 13.18C14.64 13.1 14.48 13.06 14.31 13.31C14.15 13.56 13.68 14.11 13.54 14.27C13.4 14.44 13.26 14.46 13 14.33C12.76 14.21 11.96 13.95 11 13.1C10.26 12.44 9.76 11.62 9.62 11.37C9.5 11.12 9.61 11 9.73 10.87C9.84 10.76 9.98 10.58 10.1 10.44C10.23 10.31 10.27 10.21 10.35 10.04C10.43 9.87 10.39 9.73 10.33 9.61C10.27 9.5 9.77 8.26 9.56 7.77C9.36 7.29 9.16 7.35 9 7.35C8.86 7.34 8.7 7.33 8.53 7.33Z" />
    </svg>
  );
}

/** A single circular social link with a brand fill-on-hover (premium footer). */
function SocialLink({ href, label, children }: { href: string; label: string; children: ReactNode }): JSX.Element {
  return (
    <a
      aria-label={label}
      title={label}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="grid h-9 w-9 place-items-center rounded-full border border-border bg-bg text-fg-muted shadow-sm transition-[transform,background-color,border-color,color] duration-150 ease-out hover:-translate-y-0.5 hover:border-brand hover:bg-brand hover:text-brand-contrast active:scale-95 motion-reduce:transform-none"
    >
      {children}
    </a>
  );
}

/** App-store download button (Google Play / App Store) with a two-line label. */
function AppStoreButton({ href, kind, top, name }: { href: string; kind: 'android' | 'ios'; top: string; name: string }): JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2.5 rounded-xl border border-border bg-bg px-3.5 py-2 text-fg shadow-sm transition-[transform,border-color,box-shadow] duration-150 ease-out hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-md active:scale-[0.97] motion-reduce:transform-none"
    >
      <svg viewBox="0 0 24 24" className="h-6 w-6 shrink-0" fill="currentColor" aria-hidden>
        {kind === 'android' ? (
          <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
        ) : (
          <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
        )}
      </svg>
      <span className="flex flex-col text-start leading-tight">
        <span className="text-[0.6rem] uppercase tracking-wide text-fg-muted">{top}</span>
        <span className="text-sm font-semibold">{name}</span>
      </span>
    </a>
  );
}

function NavLinks({ items, className }: { items: StorefrontNavItem[]; className?: string }): JSX.Element {
  return (
    <>
      {items.map((n) =>
        n.external ? (
          <a key={n.id} href={n.href} target="_blank" rel="noopener noreferrer" className={className}>
            {n.label}
          </a>
        ) : (
          <Link key={n.id} to={n.href} className={className}>
            {n.label}
          </Link>
        ),
      )}
    </>
  );
}

export function StorefrontLayout({ children }: { children: ReactNode }): JSX.Element {
  const { slug = '' } = useParams();
  const { t, i18n } = useTranslation('cms');
  const dir = useDirection();
  const { theme, setTheme } = useTheme();
  const accent = useStoreAccent();
  const [site, setSite] = useState<StorefrontInfo | null>(null);
  const [status, setStatus] = useState<'loading' | 'ok' | 'notfound'>('loading');
  const [menuOpen, setMenuOpen] = useState(false);
  const cart = useCartStore();
  const count = cart.slug === slug ? cartCount(cart.items) : 0;
  const wishCount = useWishlistStore((s) => wishlistCount(slug, s));
  const signedIn = useCustomerAuthStore((s) => !!s.accessToken);
  const { pathname } = useLocation();

  // Close the mobile menu on any navigation (incl. browser back/forward).
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  useEffect(() => {
    setStatus('loading');
    void publicApi()
      .get<StorefrontInfo>(`/${encodeURIComponent(slug)}`)
      .then((r) => {
        // Public surface: no /currencies access — take per-currency decimals
        // from the storefront payload so formatMoney honors IQD → 0 etc.
        if (r.data.currencyDecimals) setCurrencyDecimals(r.data.currencyDecimals);
        setSite(r.data);
        setStatus('ok');
      })
      .catch(() => setStatus('notfound'));
    // Re-fetch when the content language changes (publicApi tags ?locale).
  }, [slug, i18n.language]);

  if (status === 'loading') {
    return (
      <div className="grid min-h-screen place-items-center bg-bg text-fg-muted" dir={dir}>
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" aria-hidden />
      </div>
    );
  }

  if (status === 'notfound' || !site) {
    return (
      <div className="grid min-h-screen place-items-center bg-bg px-6 text-center text-fg" dir={dir}>
        <Helmet><title>{t('store.notFound')}</title></Helmet>
        <div>
          <Store className="mx-auto h-10 w-10 text-fg-subtle" aria-hidden />
          <h1 className="mt-3 text-xl font-semibold">{t('store.notFound')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('store.notFoundHint')}</p>
        </div>
      </div>
    );
  }

  // Scope the tenant's full theme (brand colours + font) to the storefront
  // subtree only. Only validated/derived values are injected — never raw input.
  // The visitor's accent override wins over the store's brand colour (null = store default).
  const themeStyle = {
    ...deriveStoreTheme(accent ?? site.themeColor, site.secondaryColor),
    ...fontVar(site.fontFamily),
  } as CSSProperties;
  const logo = assetUrl(site.logoUrl);
  const social = site.socialLinks ?? {};
  const headerNav = site.nav.filter((n) => n.location === 'header');
  const footerNav = site.nav.filter((n) => n.location === 'footer');
  const centered = site.headerStyle === 'centered';
  const minimal = site.headerStyle === 'minimal';
  const glass = site.headerStyle === 'glass';

  return (
    <Ctx.Provider value={{ slug, site }}>
      {/* Default SEO — pages override the title/description. */}
      <Helmet>
        <html lang={site.locale} />
        <title>{site.storeName}</title>
        {site.tagline && <meta name="description" content={site.tagline} />}
      </Helmet>
      <div className="sf-anim flex min-h-screen flex-col bg-bg font-sans text-fg" dir={dir} style={themeStyle}>
        <header className={`sticky top-0 z-40 ${glass ? 'px-3 pt-3' : 'border-b border-border/70 bg-bg/70 backdrop-blur-xl supports-[backdrop-filter]:bg-bg/60'}`}>
          <div className={`mx-auto flex max-w-7xl gap-x-3 gap-y-3 px-4 sm:gap-x-6 sm:px-6 ${
            centered ? 'flex-col items-center py-4'
              : glass ? 'items-center rounded-2xl border border-border/60 bg-bg/55 py-3 shadow-lg backdrop-blur-2xl supports-[backdrop-filter]:bg-bg/40'
                : `items-center ${minimal ? 'py-3' : 'py-4'}`
          }`}>
            <Link to={`/s/${site.slug}`} onClick={() => setMenuOpen(false)} className="group flex min-w-0 shrink items-center gap-2.5">
              {logo ? (
                <img src={logo} alt={site.storeName} className={`${minimal ? 'h-9 w-9' : 'h-11 w-11'} shrink-0 rounded-xl object-cover shadow-sm ring-1 ring-border/60`} />
              ) : (
                <div className={`grid ${minimal ? 'h-9 w-9' : 'h-11 w-11'} shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand to-accent text-fg-inverse shadow-sm`}>
                  <Store className="h-5 w-5" aria-hidden />
                </div>
              )}
              <div className="min-w-0">
                <div className={`hidden truncate font-semibold tracking-tight sm:block ${minimal ? 'text-base' : 'text-lg'}`}>{site.storeName}</div>
                {site.tagline && !minimal && <div className="hidden truncate text-xs text-fg-muted xl:block">{site.tagline}</div>}
              </div>
            </Link>
            <nav className={`flex items-center gap-1.5 sm:gap-2 ${centered ? 'flex-wrap justify-center' : 'shrink-0 ms-auto'}`} aria-label="Primary">
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                aria-expanded={menuOpen}
                aria-label={t('store.menu', { defaultValue: 'Menu' })}
                className="inline-flex items-center rounded-full border border-border bg-bg p-2.5 text-fg shadow-sm transition-[transform,border-color,color] duration-150 ease-out hover:border-brand/40 active:scale-[0.95] motion-reduce:transform-none lg:hidden"
              >
                {menuOpen ? <X className="h-4 w-4" aria-hidden /> : <Menu className="h-4 w-4" aria-hidden />}
              </button>
              <div className="hidden items-center gap-1 lg:flex">
              <Link
                to={`/s/${site.slug}`}
                className="rounded-full px-3 py-2 text-sm font-medium text-fg-muted transition hover:bg-bg-subtle hover:text-fg"
              >
                {t('store.home.title', { defaultValue: 'Home' })}
              </Link>
              <Link
                to={`/s/${site.slug}/shop`}
                className="rounded-full px-3 py-2 text-sm font-medium text-fg-muted transition hover:bg-bg-subtle hover:text-fg"
              >
                {t('store.shop.title')}
              </Link>
              <Link
                to={`/s/${site.slug}/categories`}
                className="rounded-full px-3 py-2 text-sm font-medium text-fg-muted transition hover:bg-bg-subtle hover:text-fg"
              >
                {t('store.categories.title', { defaultValue: 'Categories' })}
              </Link>
              <Link
                to={`/s/${site.slug}/brands`}
                className="rounded-full px-3 py-2 text-sm font-medium text-fg-muted transition hover:bg-bg-subtle hover:text-fg"
              >
                {t('store.brands.title', { defaultValue: 'Brands' })}
              </Link>
              {headerNav.length > 0 && (
                <NavLinks items={headerNav} className="rounded-full px-3 py-2 text-sm font-medium text-fg-muted transition hover:bg-bg-subtle hover:text-fg" />
              )}
              </div>
              <StorefrontSearchModal />
              {site.enabledLocales.length > 1 && (
                <div className="hidden items-center rounded-full border border-border bg-bg p-0.5 text-xs lg:inline-flex" role="group" aria-label={t('store.language')}>
                  {site.enabledLocales.map((lng) => (
                    <button
                      key={lng}
                      type="button"
                      onClick={() => { void setLocale(lng); }}
                      aria-pressed={i18n.language.startsWith(lng)}
                      aria-label={lng}
                      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 transition ${i18n.language.startsWith(lng) ? 'bg-brand font-semibold text-brand-contrast shadow-sm' : 'text-fg-muted hover:text-fg'}`}
                    >
                      <span className="hidden sm:inline-flex">{lng === 'en' ? <UsFlag /> : lng === 'ar' ? <IqFlag /> : null}</span>
                      <span className="font-semibold">{lng === 'en' ? 'EN' : lng === 'ar' ? 'ع' : lng.toUpperCase()}</span>
                    </button>
                  ))}
                </div>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center rounded-full border border-border bg-bg p-2.5 text-fg shadow-sm transition-[transform,border-color,color] duration-150 ease-out hover:border-brand/40 active:scale-[0.95] motion-reduce:transform-none"
                    aria-label={t('store.settings.title', { defaultValue: 'Appearance' })}
                    title={t('store.settings.title', { defaultValue: 'Appearance' })}
                  >
                    <Palette className="h-4 w-4" aria-hidden />
                  </button>
                </DropdownMenuTrigger>
                {/* themeStyle re-applies the store/visitor brand vars inside the portal. */}
                <DropdownMenuContent align="end" sideOffset={8} style={themeStyle} className="w-64 p-3">
                  <div className="mb-1.5 text-[0.7rem] font-bold uppercase tracking-wide text-fg-muted">{t('store.settings.appearance', { defaultValue: 'Appearance' })}</div>
                  <div className="mb-3 grid grid-cols-2 gap-2">
                    {(['light', 'dark'] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setTheme(m)}
                        aria-pressed={theme === m}
                        className={`inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition ${theme === m ? 'border-brand bg-brand/10 text-brand' : 'border-border text-fg-muted hover:bg-bg-subtle'}`}
                      >
                        {m === 'light' ? <Sun className="h-4 w-4" aria-hidden /> : <Moon className="h-4 w-4" aria-hidden />}
                        {t(`store.settings.${m}`, { defaultValue: m === 'light' ? 'Light' : 'Dark' })}
                      </button>
                    ))}
                  </div>
                  <div className="mb-1.5 text-[0.7rem] font-bold uppercase tracking-wide text-fg-muted">{t('store.settings.accent', { defaultValue: 'Accent color' })}</div>
                  <div className="flex flex-wrap items-center gap-2">
                    {SF_ACCENTS.map((c) => {
                      const active = (accent ?? '').toLowerCase() === c.toLowerCase();
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setStoreAccent(c)}
                          aria-label={c}
                          aria-pressed={active}
                          className={`grid h-7 w-7 place-items-center rounded-full ring-2 ring-offset-2 ring-offset-bg transition ${active ? 'ring-fg' : 'ring-transparent hover:ring-border'}`}
                          style={{ backgroundColor: c }}
                        >
                          {active && <Check className="h-3.5 w-3.5 text-white drop-shadow" aria-hidden />}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => setStoreAccent(null)}
                      className={`inline-flex h-7 items-center rounded-full border px-2.5 text-xs font-medium transition ${accent == null ? 'border-brand text-brand' : 'border-border text-fg-muted hover:bg-bg-subtle'}`}
                      title={t('store.settings.storeDefault', { defaultValue: 'Store default' })}
                    >
                      {t('store.settings.reset', { defaultValue: 'Default' })}
                    </button>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Phone+password login works without the OTP module — the account entry
                  is always visible (OTP only adds the WhatsApp-code path on the page). */}
              <Link
                to={signedIn ? '/portal/dashboard' : `/s/${site.slug}/login`}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-bg px-3.5 py-2 text-sm font-medium text-fg shadow-sm transition-[transform,border-color,box-shadow] duration-150 ease-out hover:border-brand/40 hover:shadow active:scale-[0.97] motion-reduce:transform-none"
                aria-label={signedIn ? t('store.account', { defaultValue: 'My account' }) : t('login.signIn', { defaultValue: 'Sign in' })}
              >
                <UserCircle2 className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">{signedIn ? t('store.account', { defaultValue: 'My account' }) : t('login.signIn', { defaultValue: 'Sign in' })}</span>
              </Link>
              {site.wishlistEnabled && (
                <Link
                  to={`/s/${site.slug}/wishlist`}
                  className="relative hidden items-center rounded-full border border-border bg-bg p-2.5 text-fg shadow-sm transition-[transform,border-color,color] duration-150 ease-out hover:border-brand/40 hover:text-danger active:scale-[0.95] motion-reduce:transform-none lg:inline-flex"
                  aria-label={t('store.wishlist.title', { defaultValue: 'Wishlist' })}
                >
                  <Heart className="h-4 w-4" aria-hidden />
                  {wishCount > 0 && (
                    <span key={wishCount} className="sf-pop absolute -end-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-danger px-1 text-xs font-bold text-white">{wishCount}</span>
                  )}
                </Link>
              )}
              <Link
                to={`/s/${site.slug}/cart`}
                className="relative inline-flex items-center gap-2 rounded-full border border-border bg-bg px-3.5 py-2 text-sm font-medium text-fg shadow-sm transition-[transform,border-color,box-shadow] duration-150 ease-out hover:border-brand/40 hover:shadow active:scale-[0.97] motion-reduce:transform-none"
                aria-label={t('cart.title')}
              >
                <ShoppingCart className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">{t('cart.title')}</span>
                {count > 0 && (
                  <span key={count} className="sf-pop grid h-5 min-w-5 place-items-center rounded-full bg-accent px-1 text-xs font-bold text-accent-contrast">
                    {count}
                  </span>
                )}
              </Link>
            </nav>
          </div>
          {/* Mobile nav menu (the desktop text links live behind this below lg). */}
          {menuOpen && (
            <div className="border-t border-border bg-bg lg:hidden">
              <nav className="mx-auto flex max-w-7xl flex-col gap-0.5 px-4 py-2 sm:px-6" aria-label="Mobile">
                <Link to={`/s/${site.slug}`} onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-fg-muted transition hover:bg-bg-subtle hover:text-fg">{t('store.home.title', { defaultValue: 'Home' })}</Link>
                <Link to={`/s/${site.slug}/shop`} onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-fg-muted transition hover:bg-bg-subtle hover:text-fg">{t('store.shop.title')}</Link>
                <Link to={`/s/${site.slug}/categories`} onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-fg-muted transition hover:bg-bg-subtle hover:text-fg">{t('store.categories.title', { defaultValue: 'Categories' })}</Link>
                <Link to={`/s/${site.slug}/brands`} onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-fg-muted transition hover:bg-bg-subtle hover:text-fg">{t('store.brands.title', { defaultValue: 'Brands' })}</Link>
                {headerNav.map((n) => (n.external ? (
                  <a key={n.id} href={n.href} target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-fg-muted transition hover:bg-bg-subtle hover:text-fg">{n.label}</a>
                ) : (
                  <Link key={n.id} to={n.href} onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-fg-muted transition hover:bg-bg-subtle hover:text-fg">{n.label}</Link>
                )))}

                {/* Favorite + language (moved here from the bar on mobile) */}
                <div className="my-1 border-t border-border" />
                {site.wishlistEnabled && (
                  <Link to={`/s/${site.slug}/wishlist`} onClick={() => setMenuOpen(false)} className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-fg-muted transition hover:bg-bg-subtle hover:text-fg">
                    <Heart className="h-4 w-4" aria-hidden />
                    {t('store.wishlist.title', { defaultValue: 'Wishlist' })}
                    {wishCount > 0 && <span className="ms-auto grid h-5 min-w-5 place-items-center rounded-full bg-danger px-1.5 text-xs font-bold text-white">{wishCount}</span>}
                  </Link>
                )}
                {site.enabledLocales.length > 1 && (
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-sm font-medium text-fg-muted">{t('store.language')}</span>
                    <div className="inline-flex items-center rounded-full border border-border bg-bg p-0.5 text-xs" role="group" aria-label={t('store.language')}>
                      {site.enabledLocales.map((lng) => (
                        <button key={lng} type="button" onClick={() => { void setLocale(lng); setMenuOpen(false); }} aria-pressed={i18n.language.startsWith(lng)} aria-label={lng} className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 transition ${i18n.language.startsWith(lng) ? 'bg-brand font-semibold text-brand-contrast shadow-sm' : 'text-fg-muted hover:text-fg'}`}>
                          {lng === 'en' ? <UsFlag /> : lng === 'ar' ? <IqFlag /> : null}
                          <span className="font-semibold">{lng === 'en' ? 'EN' : lng === 'ar' ? 'ع' : lng.toUpperCase()}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </nav>
            </div>
          )}
        </header>

        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6">
          <RouteTransition>{children}</RouteTransition>
        </main>

        <footer className="border-t border-border bg-bg-subtle text-sm text-fg-muted">
          {/* Subtle brand-tinted top accent */}
          <div className="h-0.5 w-full bg-gradient-to-r from-brand/0 via-brand/60 to-brand/0" aria-hidden />
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-8 gap-y-10 px-4 py-12 sm:px-6 lg:grid-cols-4">
            {/* Store identity + follow us */}
            <div className="col-span-2 flex flex-col gap-3 lg:col-span-1">
              <Link to={`/s/${site.slug}`} className="flex items-center gap-2.5">
                {logo ? (
                  <img src={logo} alt={site.storeName} className="h-10 w-10 shrink-0 rounded-xl object-cover ring-1 ring-border/60" />
                ) : (
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand to-accent text-fg-inverse"><Store className="h-5 w-5" aria-hidden /></div>
                )}
                <span className="text-base font-semibold text-fg">{site.storeName}</span>
              </Link>
              {site.tagline && <p className="max-w-xs text-sm leading-relaxed">{site.tagline}</p>}
              {(social.website || social.instagram || social.facebook || social.whatsapp) && (
                <div className="mt-1.5">
                  <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-fg">{t('store.footer.followUs', { defaultValue: 'Follow us' })}</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    {social.website && <SocialLink href={safeHref(social.website)} label={t('store.website')}><Globe className="h-4 w-4" aria-hidden /></SocialLink>}
                    {social.instagram && <SocialLink href={safeHref(social.instagram)} label="Instagram"><IgIcon className="h-4 w-4" /></SocialLink>}
                    {social.facebook && <SocialLink href={safeHref(social.facebook)} label="Facebook"><FbIcon className="h-4 w-4" /></SocialLink>}
                    {social.whatsapp && <SocialLink href={waHref(social.whatsapp)} label="WhatsApp"><WaIcon className="h-4 w-4" /></SocialLink>}
                  </div>
                </div>
              )}
            </div>

            {/* Quick links */}
            <nav className="flex flex-col gap-2.5">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-fg">{t('store.footer.quickLinks')}</h3>
              <Link to={`/s/${site.slug}`} className="w-fit transition-colors hover:text-brand">{t('store.home.title', { defaultValue: 'Home' })}</Link>
              <Link to={`/s/${site.slug}/shop`} className="w-fit transition-colors hover:text-brand">{t('store.shop.title')}</Link>
              <Link to={`/s/${site.slug}/categories`} className="w-fit transition-colors hover:text-brand">{t('store.categories.title', { defaultValue: 'Categories' })}</Link>
              <Link to={`/s/${site.slug}/brands`} className="w-fit transition-colors hover:text-brand">{t('store.brands.title', { defaultValue: 'Brands' })}</Link>
            </nav>

            {/* Information (admin footer nav: About, Terms…) + contact */}
            {(footerNav.length > 0 || site.contactEmail || site.contactPhone) && (
              <div className="flex flex-col gap-2.5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-fg">{t('store.footer.information')}</h3>
                <NavLinks items={footerNav} className="w-fit transition-colors hover:text-brand" />
                {site.contactEmail && <a className="w-fit break-all transition-colors hover:text-brand" href={`mailto:${site.contactEmail}`}>{site.contactEmail}</a>}
                {site.contactPhone && <a className="w-fit transition-colors hover:text-brand" dir="ltr" href={`tel:${site.contactPhone}`}>{site.contactPhone}</a>}
              </div>
            )}

            {/* Get the app */}
            {(social.appAndroid || social.appIos) && (
              <div className="flex flex-col gap-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-fg">{t('store.footer.getApp')}</h3>
                <p className="text-xs leading-relaxed">{t('store.footer.getAppHint')}</p>
                <div className="flex flex-col gap-2">
                  {social.appAndroid && <AppStoreButton href={safeHref(social.appAndroid)} kind="android" top={t('store.footer.getOn', { defaultValue: 'Get it on' })} name="Google Play" />}
                  {social.appIos && <AppStoreButton href={safeHref(social.appIos)} kind="ios" top={t('store.footer.downloadOn', { defaultValue: 'Download on' })} name="App Store" />}
                </div>
              </div>
            )}
          </div>

          {/* Bottom bar */}
          <div className="border-t border-border/60">
            <div className="mx-auto max-w-7xl px-4 py-5 text-center text-xs text-fg-subtle sm:px-6">
              © {new Date().getFullYear()} {site.storeName} · {t('store.footer.rights')}
            </div>
          </div>
        </footer>
        {/* Scoped inside the themed subtree so toasts read in the tenant's brand. */}
        <Toaster position="top-right" richColors closeButton dir={dir} />
        {site.aiAssistantEnabled && <StorefrontAiWidget />}
      </div>
    </Ctx.Provider>
  );
}

const FLAG_CLASS = 'h-3.5 w-5 shrink-0 rounded-[2px] ring-1 ring-black/10';

/** Simplified Stars-and-Stripes for the English toggle (Tailwind-coloured, no hex). */
function UsFlag(): JSX.Element {
  return (
    <svg viewBox="0 0 20 14" className={FLAG_CLASS} preserveAspectRatio="none" aria-hidden>
      {Array.from({ length: 13 }).map((_, i) => (
        <rect key={i} x="0" y={(i * 14) / 13} width="20" height={14 / 13} className={i % 2 === 0 ? 'fill-red-600' : 'fill-white'} />
      ))}
      <rect x="0" y="0" width="9" height={(7 * 14) / 13} className="fill-blue-800" />
      {Array.from({ length: 12 }).map((_, k) => {
        const r = Math.floor(k / 3); const c = k % 3;
        return <circle key={k} cx={1.4 + c * 2.6 + (r % 2) * 1.3} cy={1 + r * 1.7} r="0.32" className="fill-white" />;
      })}
    </svg>
  );
}

/** Simplified Iraqi tricolour (red/white/black + green Takbir hint) for the Arabic toggle. */
function IqFlag(): JSX.Element {
  return (
    <svg viewBox="0 0 20 14" className={FLAG_CLASS} preserveAspectRatio="none" aria-hidden>
      <rect x="0" y="0" width="20" height="14" className="fill-white" />
      <rect x="0" y="0" width="20" height="4.667" className="fill-red-600" />
      <rect x="0" y="9.333" width="20" height="4.667" className="fill-neutral-900" />
      <path d="M5 7 q1.2 -1.5 2.5 0 t2.5 0 t2.5 0" className="stroke-green-700" strokeWidth="0.7" fill="none" strokeLinecap="round" />
    </svg>
  );
}
